(() => {
  if (customElements.get('croma-sticky-header')) {
    return;
  }

  class CromaStickyHeader extends HTMLElement {
    connectedCallback() {
      this.header = this.querySelector('.croma-header');
      this.lastScrollY = Math.max(window.scrollY, 0);
      this.scrollThreshold = Number(this.dataset.scrollThreshold || 80);
      this.scrollDelta = 6;
      this.ticking = false;
      this.overlayAtTop = this.dataset.transparentOnTop === 'true';
      this.menuDrawer = this.querySelector('[data-mobile-menu-drawer]');
      this.menuPanel = this.querySelector('[data-mobile-menu-panel]');
      this.menuTitle = this.querySelector('[data-mobile-menu-title]');
      this.menuTriggers = Array.from(this.querySelectorAll('[data-mobile-menu-open]'));
      this.menuOpen = false;
      this.menuCloseTimer = null;
      this.inertStates = [];
      this.headerWasInert = false;
      this.lockedScrollY = 0;
      this.bodyStyleState = null;
      this.lenisWasStopped = false;

      this.syncHeroLayout();
      this.heroLayoutController = new AbortController();
      ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((name) => {
        document.addEventListener(name, this.onHeroLayoutChange, { signal: this.heroLayoutController.signal });
      });

      this.syncReservedHeight();

      if (window.ResizeObserver && this.header) {
        this.resizeObserver = new ResizeObserver(this.syncReservedHeight);
        this.resizeObserver.observe(this.header);
        this.resizeObserver.observe(document.body);
      } else {
        window.addEventListener('resize', this.syncReservedHeight);
      }

      this.classList.add('is-ready');
      this.updateState(this.lastScrollY, true);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onViewportChange);
      this.addEventListener('click', this.onComponentClick);
      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      this.heroLayoutController?.abort();
      window.cancelAnimationFrame(this.heroLayoutFrame);
      this.closeMenu({ immediate: true, restoreFocus: false });

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', this.syncReservedHeight);
      }

      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onViewportChange);
      this.removeEventListener('click', this.onComponentClick);
      document.removeEventListener('keydown', this.onKeydown);
      window.clearTimeout(this.menuCloseTimer);
    }

    syncReservedHeight = () => {
      if (!this.header) {
        return;
      }

      const headerHeight = this.header.getBoundingClientRect().height;

      if (headerHeight > 0 && headerHeight !== this.reservedHeight) {
        this.reservedHeight = headerHeight;
        this.style.setProperty('--croma-header-reserved-height', `${headerHeight}px`);
      }
      this.syncTopOffset();
    };

    syncTopOffset() {
      const offset = Math.max(0, this.getBoundingClientRect().top);
      if (offset !== this.topOffset) {
        this.topOffset = offset;
        this.style.setProperty('--croma-header-top-offset', `${offset}px`);
      }
    }

    syncHeroLayout() {
      const firstSection = document.querySelector('#MainContent > .shopify-section');
      const containedHero = Boolean(firstSection?.querySelector('[data-contained-hero]'));
      this.overlayAtTop = this.dataset.transparentOnTop === 'true' && !containedHero;
      this.classList.toggle('croma-sticky-header--transparent-on-top', this.overlayAtTop);
    }

    onHeroLayoutChange = () => {
      window.cancelAnimationFrame(this.heroLayoutFrame);
      this.heroLayoutFrame = window.requestAnimationFrame(() => {
        this.syncHeroLayout();
        this.syncReservedHeight();
        this.lastScrollY = Math.max(window.scrollY, 0);
        this.updateState(this.lastScrollY, true);
      });
    };

    onScroll = () => {
      if (this.ticking || this.menuOpen) {
        return;
      }

      this.ticking = true;

      window.requestAnimationFrame(() => {
        const currentScrollY = Math.max(window.scrollY, 0);

        this.updateState(currentScrollY);
        this.lastScrollY = currentScrollY;
        this.ticking = false;
      });
    };

    onViewportChange = () => {
      if (window.innerWidth >= 750 && this.menuOpen) {
        this.closeMenu({ immediate: true, restoreFocus: false });
      }
    };

    onComponentClick = (event) => {
      const openTrigger = event.target.closest('[data-mobile-menu-open]');

      if (openTrigger) {
        event.preventDefault();
        this.openMenu(openTrigger);
        return;
      }

      if (event.target.closest('[data-mobile-menu-close]')) {
        this.closeMenu();
      }
    };

    onKeydown = (event) => {
      if (!this.menuOpen) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeMenu();
        return;
      }

      if (event.key === 'Tab') {
        this.trapMenuFocus(event);
      }
    };

    openMenu(trigger) {
      if (!this.menuDrawer || !this.menuPanel || this.menuOpen || window.innerWidth >= 750) {
        return;
      }

      window.clearTimeout(this.menuCloseTimer);
      this.lastMenuTrigger = trigger;
      this.menuOpen = true;
      this.menuDrawer.hidden = false;
      this.headerWasInert = this.header?.inert || false;
      this.setPageInert(true);

      if (this.header) {
        this.header.inert = true;
      }

      this.lockPageScroll();
      this.classList.add('is-menu-open');
      this.menuTriggers.forEach((menuTrigger) => {
        menuTrigger.setAttribute('aria-expanded', menuTrigger === trigger ? 'true' : 'false');
      });

      window.requestAnimationFrame(() => {
        this.menuDrawer.classList.add('is-open');
        (this.menuTitle || this.menuPanel).focus({ preventScroll: true });
      });
    }

    closeMenu({ immediate = false, restoreFocus = true } = {}) {
      if (!this.menuDrawer) {
        return;
      }

      const wasOpen = this.menuOpen;
      const hadModalState = wasOpen || Boolean(this.bodyStyleState) || this.inertStates.length > 0;

      this.menuOpen = false;
      this.menuDrawer.classList.remove('is-open');
      this.classList.remove('is-menu-open');
      this.menuTriggers.forEach((menuTrigger) => menuTrigger.setAttribute('aria-expanded', 'false'));

      if (hadModalState) {
        this.unlockPageScroll();

        if (this.header) {
          this.header.inert = this.headerWasInert;
        }

        this.setPageInert(false);
      }
      window.clearTimeout(this.menuCloseTimer);

      if (immediate) {
        this.menuDrawer.hidden = true;
      } else {
        this.menuCloseTimer = window.setTimeout(() => {
          if (!this.menuOpen) {
            this.menuDrawer.hidden = true;
          }
        }, 280);
      }

      if (wasOpen && restoreFocus) {
        this.lastMenuTrigger?.focus({ preventScroll: true });
      }
    }

    lockPageScroll() {
      if (this.bodyStyleState) {
        return;
      }

      const lenis = window.Croma?.lenis;

      this.lockedScrollY = Math.max(window.scrollY, 0);
      this.bodyStyleState = {
        position: document.body.style.position,
        insetBlockStart: document.body.style.insetBlockStart,
        width: document.body.style.width,
      };
      this.lenisWasStopped = Boolean(lenis?.isStopped);

      document.documentElement.classList.add('croma-mobile-menu-open');
      document.body.classList.add('croma-mobile-menu-open');
      document.body.style.position = 'fixed';
      document.body.style.insetBlockStart = `-${this.lockedScrollY}px`;
      document.body.style.width = '100%';
      lenis?.stop();
    }

    unlockPageScroll() {
      if (!this.bodyStyleState) {
        return;
      }

      const lenis = window.Croma?.lenis;
      const scrollY = this.lockedScrollY;

      document.documentElement.classList.remove('croma-mobile-menu-open');
      document.body.classList.remove('croma-mobile-menu-open');
      document.body.style.position = this.bodyStyleState.position;
      document.body.style.insetBlockStart = this.bodyStyleState.insetBlockStart;
      document.body.style.width = this.bodyStyleState.width;
      this.bodyStyleState = null;
      window.scrollTo(0, scrollY);
      lenis?.scrollTo(scrollY, { immediate: true, force: true });

      if (!this.lenisWasStopped) {
        lenis?.start();
      }
    }

    trapMenuFocus(event) {
      if (!this.menuPanel) {
        return;
      }

      const focusableElements = Array.from(
        this.menuPanel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.closest('[hidden]') && element.getClientRects().length > 0);

      if (!focusableElements.length) {
        event.preventDefault();
        this.menuPanel.focus({ preventScroll: true });
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (
        event.shiftKey &&
        (activeElement === firstElement || activeElement === this.menuTitle || activeElement === this.menuPanel)
      ) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && (activeElement === lastElement || !this.menuPanel.contains(activeElement))) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    setPageInert(isInert) {
      if (isInert) {
        if (this.inertStates.length) {
          return;
        }

        let sectionRoot = this;

        while (sectionRoot.parentElement && sectionRoot.parentElement !== document.body) {
          sectionRoot = sectionRoot.parentElement;
        }

        this.inertStates = Array.from(document.body.children)
          .filter((element) => element !== sectionRoot && element.tagName !== 'SCRIPT')
          .map((element) => ({ element, wasInert: element.inert }));

        this.inertStates.forEach(({ element }) => {
          element.inert = true;
        });
        return;
      }

      this.inertStates.forEach(({ element, wasInert }) => {
        element.inert = wasInert;
      });
      this.inertStates = [];
    }

    updateState(currentScrollY, isInitial = false) {
      this.syncTopOffset();
      const isAtTop = currentScrollY <= 8;
      const delta = currentScrollY - this.lastScrollY;
      const isScrollingDown = delta > this.scrollDelta;
      const isScrollingUp = delta < -this.scrollDelta;

      this.classList.toggle('is-at-top', isAtTop);

      if (isAtTop) {
        this.classList.toggle('is-fixed', this.overlayAtTop);
        this.classList.remove('is-hidden', 'is-revealed');
        return;
      }

      if (this.overlayAtTop) {
        this.classList.add('is-fixed');
      }

      if (isInitial && this.overlayAtTop) {
        this.classList.add('is-hidden');
        this.classList.remove('is-revealed');
        return;
      }

      if (isInitial) {
        this.classList.remove('is-fixed', 'is-hidden', 'is-revealed');
        return;
      }

      if (isScrollingUp && currentScrollY > this.scrollThreshold) {
        this.classList.add('is-fixed', 'is-revealed');
        this.classList.remove('is-hidden');
        return;
      }

      if (isScrollingDown || !this.classList.contains('is-fixed')) {
        this.classList.add('is-fixed', 'is-hidden');
        this.classList.remove('is-revealed');
      }
    }
  }

  customElements.define('croma-sticky-header', CromaStickyHeader);
})();
